import React, { useCallback, useEffect, useState } from 'react';
import {
  Col,
  Typography,
  Space,
  Button,
  Table,
  Spin,
  Alert,
  DatePicker,
  Tag,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  PlusOutlined,
  TeamOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ExceptionOutlined,
  IdcardOutlined,
  DollarOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { HrmsAttentionBrief } from '@/components/hr/control-tower/HrmsAttentionBrief';
import { HrmsOutlookRibbon } from '@/components/hr/control-tower/HrmsOutlookRibbon';
import { HrmsFollowUpZone } from '@/components/hr/control-tower/HrmsFollowUpZone';
import { HrmsTalentSnapshot } from '@/components/hr/control-tower/HrmsTalentSnapshot';
import {
  getHrmsDashboardActionSummary,
  emptyHrmsDashboardSummary,
  type HrmsDashboardActionSummary,
  type HrmsPreviewRow,
} from '@/services/hrmsDashboard.service';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Title, Text, Paragraph } = Typography;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const HrmsDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState<HrmsDashboardActionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOnDate, setAsOnDate] = useState<string | undefined>(undefined);

  const fetchSummary = useCallback(() => {
    setLoading(true);
    setError(null);
    getHrmsDashboardActionSummary(asOnDate ? { as_on_date: asOnDate } : undefined)
      .then(setSummary)
      .catch((e: any) => setError(e?.message ?? 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [asOnDate]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && !summary) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const s = summary ?? emptyHrmsDashboardSummary();

  const previewColumns: ColumnsType<HrmsPreviewRow> = [
    {
      title: 'Item',
      dataIndex: 'title',
      key: 'title',
      render: (v, r) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(r.link_path)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      render: (v) => v || '—',
    },
    {
      title: 'Detail',
      dataIndex: 'subtitle',
      key: 'subtitle',
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => (v ? <Tag>{v}</Tag> : '—'),
    },
    {
      title: 'Aging',
      dataIndex: 'aging_days',
      key: 'aging_days',
      width: 80,
      render: (v) => (v != null ? `${v}d` : '—'),
    },
  ];

  const payrollClosed = ['Closed', 'Paid', 'Approved'].includes(s.payroll_period_status || '');

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: token.colorBgContainer,
            paddingBottom: 8,
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              HRMS Control Tower
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              Workforce, UAE compliance, leave/OT approvals, payroll readiness, and talent — true
              counts, not preview caps.
            </Paragraph>
            <Space wrap size={[4, 4]}>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('attention-brief')}>
                Attention
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('outlook')}>
                Outlook
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('hrms-follow-up')}>
                Follow-up
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('workforce-approvals')}>
                Approvals
              </Button>
              <Text type="secondary">·</Text>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('payroll-readiness')}>
                Payroll
              </Button>
              {s.talent.enabled ? (
                <>
                  <Text type="secondary">·</Text>
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('talent-snapshot')}>
                    Talent
                  </Button>
                </>
              ) : null}
            </Space>
          </div>
          <Space wrap>
            <Space>
              <Text type="secondary">As on date:</Text>
              <DatePicker
                value={asOnDate ? dayjs(asOnDate) : dayjs()}
                onChange={(d) => setAsOnDate(d ? d.format('YYYY-MM-DD') : undefined)}
                allowClear
              />
            </Space>
            <Button icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        <HrmsAttentionBrief summary={s} />
        <HrmsOutlookRibbon summary={s} />

        <ControlTowerSectionCard zone="actions" title="Quick Actions">
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/employees/new')}>
              New employee
            </Button>
            <Button icon={<TeamOutlined />} onClick={() => navigate('/hr/employees')}>
              Employees
            </Button>
            <Button icon={<ClockCircleOutlined />} onClick={() => navigate('/hr/leaves')}>
              Leave
            </Button>
            <Button icon={<ClockCircleOutlined />} onClick={() => navigate('/hr/overtime')}>
              Overtime
            </Button>
            <Button icon={<IdcardOutlined />} onClick={() => navigate('/hr/document-expiry')}>
              Document expiry
            </Button>
            <Button icon={<SolutionOutlined />} onClick={() => navigate('/hr/contracts')}>
              Contracts
            </Button>
            <Button icon={<DollarOutlined />} onClick={() => navigate('/hr/payroll')}>
              Payroll
            </Button>
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/hr/loans')}>
              Loans
            </Button>
            {s.talent.enabled ? (
              <Button icon={<TeamOutlined />} onClick={() => navigate('/hr/recruitment')}>
                Talent Acquisition
              </Button>
            ) : null}
            <Button icon={<FileTextOutlined />} onClick={() => navigate('/hr/reports')}>
              Reports
            </Button>
          </Space>
        </ControlTowerSectionCard>

        <HrmsFollowUpZone summary={s} asOnDate={asOnDate} />

        <ControlTowerSectionCard
          zone="pendingApprovals"
          sectionId="workforce-approvals"
          title={`Workforce approvals (${s.pending_leave_count + s.pending_ot_count + s.pending_profile_change_count + s.pending_salary_revision_count})`}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ControlTowerSubQueuePanel zone="qmsGateIir" title="Pending leave" count={s.pending_leave_count}>
              {s.pending_leave.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table size="small" pagination={false} rowKey="id" columns={previewColumns} dataSource={s.pending_leave} />
              )}
              {s.pending_leave_count > s.pending_leave.length ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Showing {s.pending_leave.length} of {s.pending_leave_count} — use Follow-up for full
                  queue.
                </Text>
              ) : null}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel zone="qmsGateStage" title="Pending OT" count={s.pending_ot_count}>
              {s.pending_ot.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table size="small" pagination={false} rowKey="id" columns={previewColumns} dataSource={s.pending_ot} />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel
              zone="qmsGateFir"
              title="Profile change requests"
              count={s.pending_profile_change_count}
            >
              {s.pending_profile_changes.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={previewColumns}
                  dataSource={s.pending_profile_changes}
                />
              )}
            </ControlTowerSubQueuePanel>
            <ControlTowerSubQueuePanel
              zone="qmsApprovalPlan"
              title="Salary revisions"
              count={s.pending_salary_revision_count}
            >
              {s.pending_salary_revisions.length === 0 ? (
                <Text type="secondary">None</Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={previewColumns}
                  dataSource={s.pending_salary_revisions}
                />
              )}
            </ControlTowerSubQueuePanel>
          </Space>
        </ControlTowerSectionCard>

        <div id="payroll-readiness">
          <ControlTowerMetricStrip zone="metricsDocPrep" title="Payroll readiness">
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Period status"
                count={0}
                valueDisplay={s.payroll_enabled ? s.payroll_period_status || '—' : 'N/A'}
                icon={<DollarOutlined />}
                urgency={
                  s.payroll_enabled && s.payroll_period_status && !payrollClosed
                    ? 'attention'
                    : 'neutral'
                }
                extra={
                  s.payroll_period_label ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {s.payroll_period_label}
                    </Text>
                  ) : undefined
                }
                footerLink={{
                  label: 'Payroll',
                  onClick: () => navigate('/hr/payroll'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Unapproved timesheets"
                count={s.unapproved_timesheet_count}
                icon={<ClockCircleOutlined />}
                urgency={s.unapproved_timesheet_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Attendance',
                  onClick: () => navigate('/hr/attendance'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Draft adjustments"
                count={s.draft_adjustment_count}
                icon={<FileTextOutlined />}
                urgency={s.draft_adjustment_count > 0 ? 'attention' : 'neutral'}
                footerLink={{
                  label: 'Adjustments',
                  onClick: () => navigate('/hr/adjustments'),
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Open loan exposure"
                count={s.overdue_loan_installment_count}
                amount={s.open_loan_exposure}
                formatCurrency={formatCurrency}
                icon={<WarningOutlined />}
                urgency={s.overdue_loan_installment_count > 0 ? 'attention' : 'neutral'}
                extra={
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {s.overdue_loan_installment_count} overdue installment
                    {s.overdue_loan_installment_count === 1 ? '' : 's'}
                  </Text>
                }
                footerLink={{
                  label: 'Loans',
                  onClick: () => navigate('/hr/loans'),
                }}
              />
            </Col>
          </ControlTowerMetricStrip>
        </div>

        <ControlTowerMetricStrip zone="metricsDrafts" title="Confirmation & contracts">
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Confirmation due"
              count={s.confirmation_due_count}
              icon={<ExceptionOutlined />}
              urgency={s.confirmation_due_count > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Follow up',
                onClick: () => scrollToSection('hrms-follow-up'),
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Probation ending 30d"
              count={s.probation_ending_count}
              icon={<ClockCircleOutlined />}
              urgency={s.probation_ending_count > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Follow up',
                onClick: () => scrollToSection('hrms-follow-up'),
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Contracts ending 30d"
              count={s.contracts_ending_count}
              icon={<SolutionOutlined />}
              urgency={s.contracts_ending_count > 0 ? 'attention' : 'neutral'}
              footerLink={{
                label: 'Contracts',
                onClick: () => navigate('/hr/contracts'),
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="On leave today"
              count={s.on_leave_today_count}
              icon={<TeamOutlined />}
              urgency={s.on_leave_today_count > 0 ? 'attention' : 'neutral'}
            />
          </Col>
        </ControlTowerMetricStrip>

        <HrmsTalentSnapshot summary={s} />
      </Space>
    </div>
  );
};

export default HrmsDashboardPage;
